import { Division } from "components/common/division";
import { Heading } from "components/common/heading";
import { Link } from "components/common/link";
import { Time } from "components/common/time";

import { Page } from "types";

/**
 * Repräsentiert ein Suchergebnis.
 *
 * @example
 * Das folgende Beispiel erstellt ein Suchergebnis.
 *
 * Erstelle zuerst eine Seite.
 *
 * ```typescript
 * const page: Page = {
 *     title: "Lorem Ipsum",
 *     path: "/lorem-ipsum",
 *     date: "2022-01-01"
 * };
 * ```
 *
 * Erstelle dann ein neues Suchergebnis.
 *
 * ```typescript
 * import { Result } from "components/result";
 *
 * const result = new Result(page);
 * ```
 *
 * Rendere nun das Suchergebnis in das DOM.
 *
 * ```typescript
 * document.getElementById("root")
 *     ?.replaceChildren(result.render());
 * ```
 *
 * @public
 */
class Result extends Division {
  /**
   * Das HTML Element für die Überschrift.
   */
  private heading: Heading;

  /**
   * Das HTML Element für den Link.
   */
  private link: Link;

  /**
   * Das HTML Element für das Datum der letzten Bearbeitung.
   */
  private time: Time;

  /**
   * Erstellt ein neues Suchergebnis.
   *
   * @param page Eine Seite.
   */
  constructor(page: Page) {
    super();

    /* Erzeuge die erforderlichen HTML Elemente. */
    this.heading = new Heading("h3");
    this.link = new Link();
    this.time = new Time();

    /* Füge die CSS Klassen hinzu. */
    this.withClass("result");

    this.heading.withClass("heading");
    this.link.withClass("link");
    this.time.withClass("time");

    /* Füge die Beschriftungen hinzu. */
    this.link.withHTML(page.title);
    this.time.withText(
      new Date(page.date).toLocaleDateString("de-DE", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    );

    /* Füge die maschinenlesbare Zeitangabe hinzu. */
    this.time.withDateTime(page.date);

    /* Füge die URL hinzu. */
    this.link.withURL(page.path);
  }

  /**
   * Gibt das Suchergebnis zurück.
   *
   * @returns Das Suchergebnis.
   * @override
   * @public
   */
  public render() {
    const { heading, link, time } = this;

    /* Erstelle das HTML Markup. */
    this.withChildren(heading.withChildren(link), time);

    return this.element;
  }
}

export { Result };
